import { useCallback, useEffect, useState } from "react";

/**
 * Bridges the dashboard sidebar's visibility to an existing hamburger button
 * (Bootstrap collapse on the given target id — defaults to the SecondHeader's
 * #navbarSupportedContent, pass "adminNavbar" for the SuperAdmin header), so
 * on tablet/mobile widths the sidebar slides in as an off-canvas panel
 * instead of always being stacked open above the page content.
 *
 * Pass a ref to the sidebar's wrapping element — clicking a link inside it
 * (e.g. navigating to another dashboard page) closes the menu again.
 * Also returns `closeSidebar`, for a backdrop's click-to-close.
 */
const useDashboardSidebarToggle = (sidebarRef, targetId = "navbarSupportedContent") => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const target = document.getElementById(targetId);
    if (!target) return;

    // Listen for the "start" events (show/hide), not "shown"/"hidden" — those
    // only fire once Bootstrap's own collapse animation finishes, which
    // added a ~350ms dead delay before the drawer even began sliding in.
    const handleShow = () => setSidebarOpen(true);
    const handleHide = () => setSidebarOpen(false);

    target.addEventListener("show.bs.collapse", handleShow);
    target.addEventListener("hide.bs.collapse", handleHide);

    return () => {
      target.removeEventListener("show.bs.collapse", handleShow);
      target.removeEventListener("hide.bs.collapse", handleHide);
    };
  }, [targetId]);

  const closeSidebar = useCallback(() => {
    const target = document.getElementById(targetId);
    const bsCollapse = target && window.bootstrap?.Collapse?.getInstance(target);
    if (bsCollapse) {
      bsCollapse.hide();
    } else {
      setSidebarOpen(false);
    }
  }, [targetId]);

  useEffect(() => {
    const sidebarEl = sidebarRef?.current;
    if (!sidebarEl) return;

    const closeOnLinkClick = (event) => {
      if (!event.target.closest("a")) return;
      closeSidebar();
    };

    sidebarEl.addEventListener("click", closeOnLinkClick);
    return () => sidebarEl.removeEventListener("click", closeOnLinkClick);
  }, [sidebarRef, closeSidebar]);

  return { sidebarOpen, closeSidebar };
};

export default useDashboardSidebarToggle;
